'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * Fades and lifts its children into view once, when they enter the viewport.
 *
 * Deliberately progressive: the server-rendered markup carries NO hidden state,
 * so content is always visible without JavaScript. The animation is only armed
 * on the client, and only for elements that are still below the fold, anything
 * already on screen is left alone, so there is no flash and no blank section if
 * hydration is slow, fails, or the user prefers reduced motion.
 */
export default function Reveal({ children, className = '', as: Tag = 'div', delay = 0 }) {
  const ref = useRef(null);
  // 'idle', server / no JS: visible
  // 'armed', below the fold with JS running: hidden, waiting to intersect
  // 'shown', visible (final state)
  const [state, setState] = useState('idle');

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const reduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduced || typeof IntersectionObserver === 'undefined') {
      setState('shown');
      return;
    }

    // Only arm the animation for content the user cannot see yet.
    const top = el.getBoundingClientRect().top;
    if (top < window.innerHeight * 0.9) {
      setState('shown');
      return;
    }

    setState('armed');
  }, []);

  useEffect(() => {
    if (state !== 'armed') return;
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setState('shown');
          observer.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [state]);

  const animationClass =
    state === 'armed' ? 'reveal' : state === 'shown' ? 'reveal revealed' : '';

  return (
    <Tag data-component-id="src/components/ui/Reveal.jsx:67:4" data-component-path="src/components/ui/Reveal.jsx" data-component-line="67" data-component-end-line="73" data-component-file="Reveal.jsx" data-component-name="Tag" data-component-content="%7B%22elementName%22%3A%22Tag%22%7D"
      ref={ref}
      className={`${animationClass} ${className}`}
      style={state === 'shown' && delay ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </Tag>
  );
}
